import { Box, Button, WrapItem } from '@chakra-ui/react'

import { useUser } from '@/client/context/user'

import Tippy from '@/components/UI/Tippy'

import type { ReactionState } from '../types'
import IconButtonWithNumber from '../UI/Button/IconButtonWithNumber'
import { MAX_REACTIONS_TO_SHOW } from './constants'
import { getReactionTooltipText } from './helpers'

export default function ReactionWrapItems({
  reactions,
  canUserReact,
  onToggleReaction,
  startWithShowAllButton = false,
  showAll,
  setShowAll,
}: {
  reactions: ReactionState[]
  canUserReact: boolean
  onToggleReaction: (reaction: string) => void
  startWithShowAllButton?: boolean
  showAll: boolean
  setShowAll: (showAll: boolean) => void
}): JSX.Element {
  const user = useUser()

  const showAllButton =
    reactions.length <= MAX_REACTIONS_TO_SHOW ? null : (
      <WrapItem>
        <Button
          color="fg.muted"
          minWidth="unset"
          backgroundColor="bg.muted"
          border="2px solid"
          borderColor="transparent"
          borderRadius="8px"
          height="32px"
          px="8px"
          _hover={{ cursor: 'pointer', border: '2px solid', borderColor: 'border.emphasized' }}
          _active={{ cursor: 'pointer', border: '2px solid', borderColor: 'gray.focusRing' }}
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? 'Show less' : `Show ${reactions.length - MAX_REACTIONS_TO_SHOW} more`}
        </Button>
      </WrapItem>
    )

  return (
    <>
      {startWithShowAllButton ? showAllButton : null}
      {reactions
        .slice(0, showAll ? reactions.length : MAX_REACTIONS_TO_SHOW)
        .filter(({ reactionUsers }) => reactionUsers.length > 0)
        .map(({ reaction, reactionUsers }) => {
          const userNames = reactionUsers.map(item => item.user.displayName)
          const tooltipText = getReactionTooltipText(userNames, reaction)

          return (
            <WrapItem key={reaction}>
              <Tippy disabled={!canUserReact} content={tooltipText}>
                <Box>
                  <IconButtonWithNumber
                    isDisabled={!canUserReact}
                    icon={reaction}
                    onClick={() => onToggleReaction(reaction)}
                    label={reactionUsers.length}
                    highlighted={reactionUsers.some(
                      reactionUser => reactionUser.user.id === user.id
                    )}
                  />
                </Box>
              </Tippy>
            </WrapItem>
          )
        })}
      {startWithShowAllButton ? null : showAllButton}
    </>
  )
}
